import { ProductsEtaContextProvider } from '@app/components/products/ProductsEta';
import { Button } from '@app/components/various/Button';
import { EmptyState } from '@app/components/various/EmptyState';
import { Remove } from '@app/components/various/Icons';
import { Skeleton } from '@app/components/various/Skeleton';
import { SkeletonLayout } from '@app/components/various/SkeletonLayout';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsStockTypeSeparationEnabled, getOrderDetails } from '@app/ducks';
import { getUniqProductIds } from '@app/logic/Orders';
import { useAllProductsLink, useIsScreenSize, usePageScrolling, useRemoveAllProducts } from '@app/utils/hooks';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { OrderProductSkeleton } from '../OrderProduct/OrderProductSkeleton';

import { OrderProductList } from './OrderProductList';
import { useOrderProductsContext } from './OrderProductsContext';
import { OrderProductsSorting } from './OrderProductsSorting';

const SKELETONS_COUNT = 6;
interface Props {
  isLoading: boolean;
}
export const OrderProducts = React.memo(({ isLoading }: Props) => {
  const { sortBySKU, sortByByDeliveryWindow, setSortByValue, sortByValue, sortedProducts, orderDeliveryWindows } =
    useOrderProductsContext();
  const { t } = useTranslation(['common', 'orders', 'products', 'selections']);
  const orderDetails = useSelector(getOrderDetails);
  const isStockTypeSeparationEnabled = useSelector(getIsStockTypeSeparationEnabled);
  const { removeAllProducts, canRemoveAllProducts } = useRemoveAllProducts();
  const productsOrder = getUniqProductIds(orderDetails);
  const { scrollToTop } = usePageScrolling();
  const allProductsLink = useAllProductsLink(orderDetails.order.order);
  const hasProducts = !isLoading && !isEmpty(productsOrder);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const orderDeliveryWindowIds = React.useMemo(
    () => orderDeliveryWindows.map(orderDeliveryWindow => orderDeliveryWindow.deliveryWindow),
    [orderDeliveryWindows],
  );

  React.useEffect(() => {
    scrollToTop();
  }, [sortBySKU, sortByByDeliveryWindow, scrollToTop]);

  return (
    <div>
      {isLoading && isStockTypeSeparationEnabled && (
        <div className="mx-auto mt-12 max-w-screen-xl px-5">
          <Skeleton type="text" size="xlarge" itemClassName="max-w-80" />
        </div>
      )}
      <SkeletonLayout skeleton={<OrderProductSkeleton />} isLoading={isLoading} repeatLayout={SKELETONS_COUNT}>
        <div className="mx-auto flex items-center justify-between pb-3">
          <div className="flex items-center gap-2 sm:gap-6">
            <h2 className="heading-2">{t('selections:selection_products_header', { count: productsOrder.length })}</h2>
            {canRemoveAllProducts && (
              <Button size="medium" variant="bordered" color="secondary" icon={isMobile ? Remove : undefined} onClick={removeAllProducts}>
                {!isMobile && t('common:remove_all')}
              </Button>
            )}
          </div>
          <OrderProductsSorting value={sortByValue} onSelect={setSortByValue} />
        </div>
        {!hasProducts && (
          <EmptyState title={t('orders:no_products.title')} subtitle={t('orders:no_products.subtitle')}>
            <Button as={Link} to={allProductsLink} color="secondary" variant="bordered">
              {t('products:select_products')}
            </Button>
          </EmptyState>
        )}
        <ProductsEtaContextProvider products={sortedProducts} delwinIds={orderDeliveryWindowIds}>
          <OrderProductList />
        </ProductsEtaContextProvider>
      </SkeletonLayout>
    </div>
  );
});
